import { useState, useEffect } from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Link } from "react-router-dom";
import { ArrowRight, TrendingUp, ArrowUpRight, Globe, Bot, PhoneCall, Sparkles } from "lucide-react";
import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const colorMap = {
  "Healthcare": { bg: "bg-rose-50", text: "text-rose-600", border: "border-rose-200" },
  "Legal": { bg: "bg-amber-50", text: "text-amber-700", border: "border-amber-200" },
  "Education": { bg: "bg-blue-50", text: "text-blue-600", border: "border-blue-200" },
  "Manufacturing": { bg: "bg-indigo-50", text: "text-indigo-600", border: "border-indigo-200" },
  "Food & Beverage": { bg: "bg-orange-50", text: "text-orange-600", border: "border-orange-200" },
  "Logistics": { bg: "bg-cyan-50", text: "text-cyan-700", border: "border-cyan-200" },
  "Hospitality": { bg: "bg-violet-50", text: "text-violet-600", border: "border-violet-200" },
  "Health & Wellness": { bg: "bg-pink-50", text: "text-pink-600", border: "border-pink-200" },
  "Retail & Supermarkets": { bg: "bg-emerald-50", text: "text-emerald-600", border: "border-emerald-200" },
  "Telecom & IT Services": { bg: "bg-sky-50", text: "text-sky-600", border: "border-sky-200" },
  "Real Estate": { bg: "bg-slate-50", text: "text-slate-700", border: "border-slate-200" },
  "Finance & Accounting": { bg: "bg-teal-50", text: "text-teal-700", border: "border-teal-200" },
};

const categoryConfig = {
  "All": { icon: null, label: "All" },
  "AI Automation": { icon: Sparkles, label: "AI Automation" },
  "Website": { icon: Globe, label: "Website" },
  "Chatbot": { icon: Bot, label: "Chatbot" },
  "Voice Agent": { icon: PhoneCall, label: "Voice Agent" },
};

export default function CaseStudiesPage() {
  const [caseStudies, setCaseStudies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeCategory, setActiveCategory] = useState("All");

  useEffect(() => {
    const fetchCaseStudies = async () => {
      try {
        const response = await axios.get(`${API}/case-studies`);
        setCaseStudies(response.data);
      } catch (err) {
        console.error("Failed to fetch case studies:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchCaseStudies();
  }, []);

  const filtered = activeCategory === "All"
    ? caseStudies
    : caseStudies.filter((s) => s.category === activeCategory);

  return (
    <div data-testid="case-studies-page">
      {/* Header */}
      <section className="page-header-gradient border-b border-slate-100 py-16 md:py-24 relative overflow-hidden">
        <div className="hero-glow absolute inset-0" />
        <div className="dot-grid absolute inset-0 opacity-30" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <Badge
            variant="outline"
            className="mb-4 px-4 py-1.5 text-blue-600 border-blue-200 bg-white/80 font-semibold backdrop-blur-sm"
          >
            Our Work
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight text-slate-900 mb-4">
            Case Studies
          </h1>
          <p className="text-base md:text-lg text-slate-600 max-w-2xl leading-relaxed">
            Real results from real clients. See how we've transformed workflows
            across multiple industries — from AI automation to websites, chatbots, and voice agents.
          </p>
        </div>
      </section>

      {/* Category Filter */}
      <section className="sticky top-16 z-40 bg-white/95 backdrop-blur-sm border-b border-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap gap-2">
            {Object.entries(categoryConfig).map(([key, { icon: Icon, label }]) => (
              <button
                key={key}
                onClick={() => setActiveCategory(key)}
                data-testid={`category-filter-${key.toLowerCase().replace(/ /g, "-")}`}
                className={`inline-flex items-center gap-1.5 px-4 py-2 rounded-full text-sm font-medium border transition-all duration-200 ${
                  activeCategory === key
                    ? "bg-blue-600 text-white border-blue-600 shadow-sm"
                    : "bg-white text-slate-600 border-slate-200 hover:border-blue-300 hover:text-blue-600"
                }`}
              >
                {Icon && <Icon className="w-3.5 h-3.5" strokeWidth={1.5} />}
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Case Studies Grid */}
      <section className="py-12 md:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="h-72 bg-slate-100 rounded-lg animate-pulse" />
              ))}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {filtered.map((study, idx) => {
                const colors = colorMap[study.industry] || {
                  bg: "bg-slate-50",
                  text: "text-slate-600",
                  border: "border-slate-200",
                };
                return (
                  <Card
                    key={study.id}
                    className="bg-white border border-slate-200 hover:shadow-card-hover hover:border-blue-100 transition-all duration-300 group overflow-hidden card-lift"
                    data-testid={`case-study-card-${idx}`}
                  >
                    <CardContent className="p-0">
                      <div className={`p-6 ${colors.bg} border-b ${colors.border}`}>
                        <div className="flex items-center justify-between mb-3">
                          <div className="flex items-center gap-2">
                            <Badge
                              variant="outline"
                              className={`${colors.text} ${colors.border} text-xs`}
                            >
                              {study.industry}
                            </Badge>
                            {study.category && (
                              <Badge variant="outline" className="text-xs text-blue-600 border-blue-200 bg-blue-50">
                                {study.category}
                              </Badge>
                            )}
                          </div>
                          <ArrowUpRight className="w-4 h-4 text-slate-400 group-hover:text-blue-600 transition-colors" />
                        </div>
                        <h3 className="font-heading text-xl font-semibold text-slate-900 mb-1">
                          {study.title}
                        </h3>
                        <p className="text-sm text-slate-500 italic">{study.client}</p>
                      </div>

                      <div className="p-6">
                        <div className="mb-4">
                          <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1.5">
                            Challenge
                          </p>
                          <p className="text-sm text-slate-600 leading-relaxed">
                            {study.challenge}
                          </p>
                        </div>

                        <div className="mb-4">
                          <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-1.5">
                            Solution
                          </p>
                          <p className="text-sm text-slate-700 leading-relaxed font-medium">
                            {study.solution}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs font-medium uppercase tracking-wider text-slate-400 mb-2">
                            Results
                          </p>
                          <ul className="space-y-1.5">
                            {study.results.map((result, rIdx) => (
                              <li
                                key={rIdx}
                                className="flex items-center gap-2 text-sm text-slate-600"
                              >
                                <TrendingUp className="w-3.5 h-3.5 text-teal-600 shrink-0" />
                                {result}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 section-gradient-light border-t border-slate-100">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl md:text-3xl font-semibold text-slate-900 mb-3">
            Want results like these?
          </h2>
          <p className="text-sm text-slate-600 mb-6">
            Every case study started with a single conversation. Let's start yours.
          </p>
          <Link to="/contact">
            <Button
              data-testid="case-studies-cta"
              className="bg-blue-600 text-white hover:bg-blue-700 rounded-md px-8 py-3 h-auto font-semibold"
              size="lg"
            >
              Book Your Free Consultation
              <ArrowRight className="w-4 h-4 ml-1" />
            </Button>
          </Link>
        </div>
      </section>
    </div>
  );
}
